import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  ALL_CO_SUBSTEPS,
  CO_BASE,
  frontierSlug,
  isSlugReachable,
  isValidSlug,
} from "../../domain/steps";
import type { CoClient } from "../../domain/types";
import {
  ensureLoaded,
  setCurrentSlug,
  useCoClient,
  useCoLoadState,
  useCoProgress,
} from "../../data/store";
import { CoButton } from "../../components/Button";
import { OnboardingLayout } from "../OnboardingLayout";
import { CompanyDetailsStep } from "../steps/CompanyDetailsStep";
import { BooksQuestionsStep } from "../steps/BooksQuestionsStep";
import { AccountStartDateStep } from "../steps/AccountStartDateStep";
import { PackageSizingStep } from "../steps/PackageSizingStep";
import { ZohoAccountStep } from "../steps/ZohoAccountStep";
import { AddZournalStep } from "../steps/AddZournalStep";
import { ConfirmConnectionStep } from "../steps/ConfirmConnectionStep";
import { ConnectTallyStep } from "../steps/ConnectTallyStep";
import { GstReviewStep } from "../steps/GstReviewStep";
import { LedgersStep } from "../steps/LedgersStep";
import { RoundingOffStep } from "../steps/RoundingOffStep";
import { TallyRoundingOffStep } from "../steps/TallyRoundingOffStep";
import { TallyLedgersStep } from "../steps/TallyLedgersStep";
import { TallyGstStep } from "../steps/TallyGstStep";
import { TallyTdsStep } from "../steps/TallyTdsStep";
import { TallyCurrencyStep } from "../steps/TallyCurrencyStep";
import { TallyBanksStep } from "../steps/TallyBanksStep";
import { TallyCustomersVendorsStep } from "../steps/TallyCustomersVendorsStep";
import { TallyOpeningBalancesStep } from "../steps/TallyOpeningBalancesStep";
import { TallyPrepareStep } from "../steps/TallyPrepareStep";
import { TdsTcsReviewStep } from "../steps/TdsTcsReviewStep";
import { BanksReviewStep } from "../steps/BanksReviewStep";
import { CustomersVendorsStep } from "../steps/CustomersVendorsStep";
import { CurrencyStep } from "../steps/CurrencyStep";
import { OpeningBalancesStep } from "../steps/OpeningBalancesStep";
import { PrepareZohoStep } from "../steps/PrepareZohoStep";
import { TeamAssignmentStep } from "../steps/TeamAssignmentStep";
import { EscalationsAccessStep } from "../steps/EscalationsAccessStep";
import { CatchUpTrainingStep } from "../steps/CatchUpTrainingStep";
import { MonthCloseChecklistStep } from "../steps/MonthCloseChecklistStep";
import { StartDateStep } from "../steps/StartDateStep";
import { PlaceholderStep } from "../steps/PlaceholderStep";
import "../../styles.css";

// Sub-steps built for real. Everything else in the catalog renders as a
// template that still navigates, so the flow is walkable end to end.
const STEP_COMPONENTS: Record<
  string,
  React.ComponentType<{ clientId: string }>
> = {
  "company-details": CompanyDetailsStep,
  "books-questions": BooksQuestionsStep,
  "account-start-date": AccountStartDateStep,
  "package-sizing": PackageSizingStep,
  "zoho-account": ZohoAccountStep,
  "add-zournal": AddZournalStep,
  "confirm-connection": ConfirmConnectionStep,
  "connect-tally": ConnectTallyStep,
  ledgers: LedgersStep,
  "rounding-off": RoundingOffStep,
  "gst-review": GstReviewStep,
  "tds-tcs-review": TdsTcsReviewStep,
  "banks-review": BanksReviewStep,
  "customers-vendors": CustomersVendorsStep,
  currency: CurrencyStep,
  "opening-balances": OpeningBalancesStep,
  "prepare-zoho": PrepareZohoStep,
  "team-assignment": TeamAssignmentStep,
  "escalations-access": EscalationsAccessStep,
  "month-close-checklist": MonthCloseChecklistStep,
  "catch-up-training": CatchUpTrainingStep,
  "start-date": StartDateStep,
};

function stepTitle(slug: string): string {
  const sub = ALL_CO_SUBSTEPS.find((candidate) => candidate.slug === slug);
  return sub ? sub.title : slug;
}

function liveSince(liveAt: string): string {
  const at = new Date(liveAt.includes("T") ? liveAt : liveAt.replace(" ", "T"));
  if (Number.isNaN(at.getTime())) return liveAt;

  return at.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * The wizard, once the client is live: readable, not editable.
 *
 * The tenant owns a live client's settings. Master's steps are one-shot — the
 * go-live sequence skips any step already marked done and nothing resets one —
 * so an edit here would change master's own tables and never reach the client,
 * and the verify-setup steps would push straight into the live Zoho org. The
 * lock is the banner plus `<fieldset disabled>` (see .co-live-lock in
 * styles.css); the left nav stays live so every step is still walkable.
 */
function LiveBanner({ client }: { client: CoClient }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-primary/30 bg-primary/5 px-4 py-3">
      <p className="text-sm font-medium">
        Live since {liveSince(client.liveAt as string)}.
      </p>
      <p className="text-sm text-muted-foreground">
        Settings are now managed on the tenant.
      </p>
    </div>
  );
}

export function OnboardingStepPage() {
  const { clientId, step } = useParams<{ clientId: string; step: string }>();
  const navigate = useNavigate();
  const client = useCoClient(clientId ?? "");
  const progress = useCoProgress(clientId ?? "");
  const { error } = useCoLoadState(clientId ?? "");

  // A URL pointing past the flow's frontier (a bookmark, a typed address, a
  // stale tab) lands on the step the flow is actually waiting on instead.
  const blocked = Boolean(
    client &&
    step &&
    isValidSlug(step) &&
    !isSlugReachable(client, progress, step),
  );

  useEffect(() => {
    if (clientId) ensureLoaded(clientId);
  }, [clientId]);

  useEffect(() => {
    if (blocked && client && clientId) {
      navigate(`${CO_BASE}/${clientId}/${frontierSlug(client, progress)}`, {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked, clientId]);

  useEffect(() => {
    if (clientId && step && client && isValidSlug(step) && !blocked) {
      setCurrentSlug(clientId, step);
    }
  }, [clientId, step, client, blocked]);

  if (!clientId || !step) return null;

  // Branching steps (Tally vs Zoho) read `client.backend.system`. Rendering
  // before the draft is in memory picks the Zoho customers step, which has
  // nothing for a Tally company and stays on 0 until the page is reloaded.
  if (!client) {
    if (error) {
      return (
        <div className="co-root flex h-screen flex-col items-center justify-center gap-3 p-16 text-center">
          <h1 className="text-xl font-semibold tracking-tight">
            Couldn't load this client
          </h1>
          <p className="max-w-md text-sm text-muted-foreground">{error}</p>
          <CoButton variant="outline" onClick={() => navigate(CO_BASE)}>
            Back to client onboarding
          </CoButton>
        </div>
      );
    }

    return (
      <div className="co-root flex h-screen items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading this client…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="co-root flex h-screen flex-col items-center justify-center gap-3 p-16 text-center">
        <h1 className="text-xl font-semibold tracking-tight">
          Couldn't load this client
        </h1>
        <p className="max-w-md text-sm text-muted-foreground">{error}</p>
        <CoButton variant="outline" onClick={() => navigate(CO_BASE)}>
          Back to client onboarding
        </CoButton>
      </div>
    );
  }

  if (!isValidSlug(step)) {
    return (
      <OnboardingLayout clientId={clientId} activeSlug={undefined}>
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold tracking-tight">
            Step not found
          </h2>
          <p className="text-sm text-muted-foreground">
            "{step}" isn't a step in this flow.
          </p>
        </div>
      </OnboardingLayout>
    );
  }

  // Don't flash (or run) a locked step's screen while the redirect lands.
  if (blocked) return null;

  const StepComponent =
    client?.backend.system === "tally" && step === "ledgers"
      ? TallyLedgersStep
      : client?.backend.system === "tally" && step === "rounding-off"
        ? TallyRoundingOffStep
        : client?.backend.system === "tally" && step === "gst-review"
          ? TallyGstStep
          : client?.backend.system === "tally" && step === "tds-tcs-review"
            ? TallyTdsStep
            : client?.backend.system === "tally" && step === "currency"
              ? TallyCurrencyStep
              : client?.backend.system === "tally" && step === "banks-review"
                ? TallyBanksStep
                : client?.backend.system === "tally" &&
                    step === "customers-vendors"
                  ? TallyCustomersVendorsStep
                  : client?.backend.system === "tally" &&
                      step === "opening-balances"
                    ? TallyOpeningBalancesStep
                    : client?.backend.system === "tally" &&
                        step === "prepare-zoho"
                      ? TallyPrepareStep
                      : STEP_COMPONENTS[step];
  const isLive = Boolean(client && client.liveAt);
  const body = StepComponent ? (
    <StepComponent clientId={clientId} />
  ) : (
    <PlaceholderStep clientId={clientId} slug={step} title={stepTitle(step)} />
  );

  return (
    <OnboardingLayout clientId={clientId} activeSlug={step}>
      {isLive && client ? (
        <>
          <LiveBanner client={client} />
          {/* The go-live step keeps its run log — a disabled fieldset hides
              nothing, it only stops the controls inside it. */}
          <fieldset disabled className="co-live-lock">
            {body}
          </fieldset>
        </>
      ) : (
        body
      )}
    </OnboardingLayout>
  );
}
